import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { reduxForm } from 'redux-form';

import type LocationRequestModel from 'ab-requestModels/location.requestModel';

import { toRawPhoneNumber } from '@acceligentllc/shared/utils/phone';

import { CountryCode } from '@acceligentllc/shared/enums/countryCode';

import type { RootState } from 'af-reducers';

import { LOCATION_CREATE } from 'af-constants/reduxForms';
import CLIENT from 'af-constants/routes/client';

import Breadcrumbs from 'af-components/Breadcrumbs';
import CreateLocationForm from 'af-components/SharedForms/Resources/Location';
import { validate } from 'af-components/SharedForms/Resources/Location/validation';

import * as LocationActions from 'af-actions/location';

type OwnProps = CustomRouteComponentProps<{ id?: string; }>;

type FormOwnProps = OwnProps & ConnectedProps<typeof connector>;

type Props = FormOwnProps & InjectedFormProps<LocationRequestModel, FormOwnProps>;

const Create: React.FC<Props> = (props) => {
	const { history, createLocation, location: { state: { orgAlias } }, companyName } = props;

	const onCreateLocation = React.useCallback(async (form: LocationRequestModel) => {
		const data: LocationRequestModel = {
			...form,
			phone: toRawPhoneNumber(form.phone),
			fax: form.fax ? toRawPhoneNumber(form.fax) : undefined,
		};
		const isLocationCreated = await createLocation(data);

		if (isLocationCreated) {
			history.push(CLIENT.COMPANY.RESOURCES.LOCATION.LIST(orgAlias, companyName));
		}
	}, [companyName, createLocation, history, orgAlias]);

	return (
		<div className="form-segment">
			<Breadcrumbs
				items={[
					{ label: 'Locations', url: CLIENT.COMPANY.RESOURCES.LOCATION.LIST(orgAlias, companyName) },
					{ label: 'New Location' },
				]}
			/>
			<CreateLocationForm
				{...props}
				onSubmit={onCreateLocation}
			/>
		</div>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;
	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		companyName: companyData.name,
	};
}

function mapDispatchToProps() {
	return {
		createLocation: LocationActions.create,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(
	React.memo,
	connector,
	reduxForm<LocationRequestModel, FormOwnProps>({
		form: LOCATION_CREATE,
		validate,
		enableReinitialize: true,
		initialValues: {
			showInStatistics: false,
			phoneCountryCode: CountryCode.US,
			faxCountryCode: CountryCode.US,
		},
	})
);

export default enhance(Create);
